import type { StaffRole } from "@lafayette/auth";
import { useMemo } from "react";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";
import { useStaffRoles } from "../access/use-staff-roles";

// What a staff member may do in the customer memory UI. This is presentation only: the API decides every
// request again (a staff member also needs an assignment to the customer, or the supervisor role, and the
// API answers 403 without telling which). The roles come from the domain's `capabilityRoles`.

export type MemoryCapabilities = Readonly<{
  /** `customer.read_assigned` or `customer.read_all`: the customer list. */
  listCustomers: boolean;
  /** `memory.read`: the workspace and the fact detail. */
  read: boolean;
  /** `memory.manage`: add, activate, reject, accept, confirm, dispute, resolve, correct, delete. */
  manage: boolean;
}>;

const memoryCapabilityRules = {
  listCustomers: ["customer.read_assigned", "customer.read_all"],
  read: "memory.read",
  manage: "memory.manage",
} as const satisfies CapabilityRules;

export function memoryCapabilitiesForRoles(roles: readonly StaffRole[]): MemoryCapabilities {
  return capabilityFlags(memoryCapabilityRules, roles);
}

export function useMemoryCapabilities(): Readonly<{
  capabilities: MemoryCapabilities;
  isLoading: boolean;
}> {
  const { roles, isLoading } = useStaffRoles();
  const capabilities = useMemo(() => memoryCapabilitiesForRoles(roles), [roles]);
  return { capabilities, isLoading };
}
